import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const MAX_POLLS = 10;
const POLL_MS = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState("polling"); // polling | paid | failed | expired
  const [error, setError] = useState("");
  const attempts = useRef(0);
  const stopped = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      setError("Missing session id");
      return;
    }

    const poll = async () => {
      if (stopped.current) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          stopped.current = true;
          await refresh();
          setState("paid");
          setTimeout(() => nav("/library"), 1500);
          return;
        }
        if (data.status === "expired") {
          stopped.current = true;
          setState("expired");
          return;
        }
        if (attempts.current >= MAX_POLLS) {
          stopped.current = true;
          setState("failed");
          setError("Payment status check timed out.");
          return;
        }
        setTimeout(poll, POLL_MS);
      } catch (e) {
        stopped.current = true;
        setState("failed");
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    };
    poll();
    return () => {
      stopped.current = true;
    };
  }, [sessionId, refresh, nav]);

  return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center">
      {state === "polling" && (
        <>
          <Loader2 className="w-10 h-10 text-[var(--forest)] mx-auto animate-spin" strokeWidth={1.5} />
          <h1 data-testid="payment-polling" className="font-serif text-4xl mt-6">Confirming your payment…</h1>
          <p className="text-[var(--ink-soft)] mt-3">Hold tight — this only takes a moment.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="w-12 h-12 text-[var(--forest)] mx-auto" strokeWidth={1.5} />
          <h1 data-testid="payment-success" className="font-serif text-5xl mt-5">The library is yours.</h1>
          <p className="text-[var(--ink-soft)] mt-3">Redirecting you to the field guide…</p>
        </>
      )}
      {(state === "failed" || state === "expired") && (
        <>
          <XCircle className="w-12 h-12 text-[var(--terracotta)] mx-auto" strokeWidth={1.5} />
          <h1 data-testid="payment-failed" className="font-serif text-4xl mt-5">Something didn't go through.</h1>
          <p className="text-[var(--ink-soft)] mt-3">{error || "Your session expired or the payment was cancelled."}</p>
          <Link to="/unlock" className="btn-accent mt-8 inline-flex">Try again</Link>
        </>
      )}
    </div>
  );
}
